import { App, Button, Flex, Modal, notification, Space, Spin, Steps, Tabs, Typography } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaleOrderItemState, Customer, SaleOrderAddItem, PriceHis, SaleOrderAdd, PriceAdd } from "@/types/sales";
import { Supplier } from "@/types/purchase";
import { Org } from "@/types/org";
import { orgList as orgListApi } from "@/apis/org";
import { getSupplierName } from "@/utils/supplier";
import SOrgSelect from "@/components/SOrgSelect";
import SelectCustomer from "@/components/Selector/SelectCustomer";
import SupplierList from "@/pages/Requirement/pages/Detail/components/MultiPurchaseSteps/components/SupplierList";
import MatchSaleProduct, { MatchSaleProductRef } from "./MatchSaleProduct";
import { Product } from "@/types/product";
import { sAdd as priceAdd } from "@/apis/price";

import styled from "styled-components";
import { useMainCurrency } from "@/layout/hooks/useMainCurrency";
import { latestPrices } from "@/apis/customer";
import { sAdd as saleOrderAdd, update } from "@/apis/order";
import SText from "@/styles/SText";
import ItemsOrderForm from "../ItemsOrderForm";
import { dp, multi, plus } from "@/utils/calc";
import getFileBuffer from "@/utils/file/getFileBuffer";
import { upload } from "@/utils/file/upload";
import { getFileInfo } from "@/apis/file";
import { BASEURL } from "@/apis";


const ContentWrap = styled.div`
  width: 100%;
  height: 70vh;
  display: flex;
  flex-direction: column;

  & .body-content {
    flex: 1;
    overflow: auto;
  }
`;

interface SaleSwapModalProps {
  open: boolean;
  originOrder: SaleOrderItemState;
  onClose: () => void;
  onSwapSuccess: () => void;
}

const SaleSwapModal: FC<SaleSwapModalProps> = ({
  open,
  originOrder,
  onClose,
  onSwapSuccess
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const { getMainCurrency } = useMainCurrency();

  const matchProductRef = useRef<MatchSaleProductRef>(null);

  // 步骤状态
  const [current, setCurrent] = useState<number>(0);

  // 供应商相关状态
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  // 组织相关状态
  const [orgListLoading, setOrgListLoading] = useState<boolean>(false);
  const [orgList, setOrgList] = useState<Org[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [lastFetchSupplierId, setLastFetchSupplierId] = useState<string>('');

  // 客户相关状态
  const [customer, setCustomer] = useState<Customer | null>(null);

  // 产品匹配相关状态
  const [matchErr, setMatchErr] = useState<boolean>(false);


  const [resSaleOrder, setResSaleOrder] = useState<SaleOrderItemState | null>(null);

  // 加载状态
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [errCodes, setErrCodes] = useState<Array<string>>([]);

  // 当进入步骤1且供应商存在时，检查是否需要请求组织列表
  useEffect(() => {
    if (supplier && current === 1) {
      if (supplier.id !== lastFetchSupplierId) {
        fetchOrgList(supplier);
      }
    }
  }, [current, supplier, lastFetchSupplierId]);

  // 获取组织列表
  const fetchOrgList = async (supp: Supplier) => {
    const supplierApi = supp?.wmsApi ?? '';
    setOrgId('');
    setOrgList([]);
    setCustomer(null);
    setLastFetchSupplierId(supp.id);
    if (!!!supplierApi) {
      notification.error({
        message: t("requirement.supplierApiError"),
        description: `${t("requirement.supplierApiLabel")}${supplierApi}`,
        placement: "top",
      });
      return;
    }

    setOrgListLoading(true);
    try {
      const res = await orgListApi(undefined, supplierApi);
      setOrgList(res.body);
    } catch {
      notification.error({
        message: t("requirement.fetchOrgInfoFailed"),
        placement: "top"
      });
    } finally {
      setOrgListLoading(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    const matchedProducts = matchProductRef.current?.getMatchedProducts() || [];
    if (matchedProducts.length > 0) {
      onSwapSuccess();
    }
    onClose();
  };

  // 上一步
  const handlePrev = () => {
    setCurrent(current - 1);
    setMatchErr(false);
  };

  // 下一步
  const handleNext = () => {
    if (originOrder.items.length === 0) {
      notification.error({
        message: t("requirement.noDataToTransfer"),
        description: t("requirement.noDataToTransferDesc"),
        placement: "top"
      });
      return;
    }

    if (!!!supplier) {
      notification.error({
        message: t("requirement.selectSupplierFirst"),
        placement: "top"
      });
      return;
    }

    if (current === 0) {
      setCurrent(current + 1);
      return;
    }

    if (!!!orgId && current > 0) {
      notification.error({
        message: t("requirement.selectSupplierOrg"),
        placement: "top"
      });
      return;
    }

    if (!!!customer && current > 0) {
      notification.error({
        message: t("requirement.selectSupplierCustomer"),
        placement: "top"
      });
      return;
    }

    // 步骤1时，确认转单
    if (current === 1) {
      if (matchErr) {
        notification.error({
          message: t("requirement.productMatchFailed"),
          placement: "top"
        });
        return;
      }

      modal.confirm({
        title: t("requirement.swapSale"),
        content: t("requirement.confirmGenerateSaleOrder"),
        onOk: handleSubmit
      });
      return;
    }

    setCurrent(current + 1);
  };

  // 提交转单
  const handleSubmit = async () => {
    if (!customer || !supplier || !!!orgId) {
      notification.error({
        message: t("requirement.selectSupplierAndCustomer"),
        placement: "top"
      });
      return;
    }

    const matchedProducts = matchProductRef.current?.getMatchedProducts() || [];
    if (matchedProducts.length === 0) {
      notification.error({
        message: t("requirement.noMatchedProductData"),
        placement: "top"
      });
      return;
    }

    setSubmitLoading(true);
    try {
      let bomFileId = '';
      if (!!originOrder.bomFileId) {
        let originFileData = await getFileInfo(originOrder.bomFileId);
        let buffer = await getFileBuffer(originOrder.bomFileId);
        let file = await upload({
          name: originFileData?.name ?? '',
          size: buffer.byteLength,
          file: new Blob([buffer])
        }, supplier.wmsApi)
        bomFileId = file?.id ?? ''
      }
      let eCodes: Array<string> = [];
      // 1、供应商的本位币
      const currencyDict = await getMainCurrency(supplier.wmsApi ?? '', [])
      // 2、匹配价格
      let { body: priceHisList } = await latestPrices(customer.id, undefined, supplier.wmsApi);
      // 3. 找出 priceHisList 中不存在的物料
      const existingProductIds = new Set(priceHisList.map(p => p.productId));
      const productsNeedAddPrice = matchedProducts.filter(p => !existingProductIds.has(p.id));
      // if (productsNeedAddPrice.length > 0) {
      //   eCodes = productsNeedAddPrice.map(p => p.code)
      //   notification.error({
      //     message: t('requirement.swapEnd'),
      //     description: (
      //       <>
      //         <div style={{ color: "red" }}>
      //           {t('requirement.noCustomerPrice')}
      //         </div>
      //         <div>{t('requirement.plAddCustomerPrice')}</div>
      //       </>
      //     )
      //   })
      //   setSubmitLoading(false)
      // } else eCodes = [];
      // if (eCodes.length > 0) return setErrCodes(eCodes);

      // 3. 批量添加 Price
      if (productsNeedAddPrice.length > 0) {
        for (const product of productsNeedAddPrice) {
          try {
            const priceAddData: PriceAdd = {
              customerId: customer.id,
              productId: product.id,
              price: '1',
              desc: '',
              estimateDays: null,
              expiredAt: null,
              mfgr: '',
              customerModel: '',
            };
            await priceAdd(priceAddData, supplier.wmsApi ?? "");
          } catch (error) {
            console.error(`Failed to add price for product ${product.code}:`, error);
          }
        }

        //   // 4. 重新获取价格历史数据
        const { body: newPriceHisList } = await latestPrices(customer.id, undefined, supplier.wmsApi);
        priceHisList = newPriceHisList;
      }

      // 4. 构建 productId -> PriceHis 的映射
      const priceHisMap = new Map<string, PriceHis>();
      for (const priceHis of priceHisList) {
        priceHisMap.set(priceHis.productId, priceHis);
      }
      let mfgr = '';
      let customerModel = '';
      // 4、转单获取item
      const saleOrderItems: SaleOrderAddItem[] = [];
      for (const item of originOrder.items) {
        let product = matchedProducts.find(mp => mp.code === item.product?.code)
        const priceHis = priceHisMap.get(product?.id ?? '');
        if (!priceHis) continue;
        mfgr = priceHis.mfgr
        customerModel = priceHis.customerModel
        let amount = multi((item.quantity || 0) + '', priceHis.price);
        let bamount = '0';
        const saleItem: SaleOrderAddItem = {
          priceHisId: priceHis.id,
          price: priceHis.price,
          remark: item.remark,
          productName: item.productName,
          quantity: item.quantity,
          discountedPrice: '0',
          amount,
          discount: '0',
          realAmount: amount,
          bquantity: item.bquantity,
          bdiscountedPrice: priceHis.price,
          bamount,
          bdiscount: '0',
          brealAmount: bamount,
          totalRealAmount: plus(amount, bamount),
        };
        saleOrderItems.push(saleItem);
      }
      let allRealAmount = '0';

      saleOrderItems.forEach(item => {
        allRealAmount = plus(plus(item.realAmount, item.brealAmount), allRealAmount)
      })
      if (saleOrderItems.length === 0) {
        setSubmitLoading(false);
        notification.error({
          message: t("requirement.saleOrderGenerateFailed"),
        })
        return
      }
      const saleOrderAddData: SaleOrderAdd = {
        docId: '',
        bomFileId,
        type: originOrder.type,
        customerId: customer.id,
        country: customer.country || '',
        taxRate: customer.taxRate || '',
        receiver: customer.shortName || customer.name1 || customer.name2 || customer.code || '',
        receiverContact: customer.contact || '',
        receiverMobile: customer.mobile || '',
        receiverAddress: customer.address || '',
        totalAmount: dp(allRealAmount),
        realAmount: allRealAmount,
        preApproval: originOrder.preApproval,
        innerNumber: originOrder?.innerNumber ?? '',
        finishState: originOrder.finishState,
        extPrice: originOrder.extPrice,
        items: saleOrderItems,
        shippingMethod: originOrder.shippingMethod ?? '',
        currency: customer.currency || originOrder.currency || currencyDict?.key,
        commissionRate: customer.commissionRate ? String(customer.commissionRate) : originOrder.commissionRate,
        otherNumber1: originOrder?.number,
        productId: originOrder.productId ? matchedProducts[0].id : null,
        preFinishDate: originOrder.preFinishDate,
        reqDeliDate: originOrder.reqDeliDate || null,
        smtStartDate: originOrder.smtStartDate || null,
        totalRealAmount: allRealAmount,
        mfgr,
        customerModel,
        urgencyLevel: originOrder.urgencyLevel || '',
        specialReq: originOrder.specialReq || '',
        sourceOrder: originOrder.number,
        sourceServer: BASEURL
      };
      let saleOrderRes = await saleOrderAdd(saleOrderAddData, orgId, supplier.wmsApi ?? '');
      await update(originOrder?.id ?? "", {
        ...originOrder,
        shippingMethod: originOrder.shippingMethod ?? '',
        discount: originOrder.discount ?? '',
        otherNumber1: saleOrderRes.number,
        mfgr: saleOrderRes?.mfgr || '',
        customerModel: saleOrderRes?.customerModel || '',
        urgencyLevel: saleOrderRes?.urgencyLevel || '',
        specialReq: saleOrderRes?.specialReq || '',
        items: []
      })
      setResSaleOrder({ ...saleOrderRes, customer });

      notification.success({
        message: t("requirement.saleOrderTransferSuccess"),
        description: `${t("requirement.saleOrderNumber")}${saleOrderRes.number}`,
        placement: "top"
      });
      setCurrent(current + 1);
    } catch (error) {
      console.error('Failed to swap sale order:', error);
      notification.error({
        message: t("requirement.saleOrderGenerateFailed"),
        placement: "top"
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // 客户选择
  const handleCustomerChange = (customerSelected: Customer) => {
    setCustomer(customerSelected);
  };

  // 按钮禁用状态
  const { disNext, disPrev } = useMemo(() => {
    let disNext = false;
    let disPrev = false;
    return {
      disNext: disNext || matchErr,
      disPrev
    };
  }, [current, supplier, orgId, matchErr]);

  // 步骤配置
  const stepsItems = useMemo(() => [
    {
      title: t("requirement.selectSupplier"),
      content: <SupplierList value={supplier} onChange={setSupplier} />,
    },
    {
      title: t("requirement.matchProduct"),
      content: (
        <MatchSaleProduct
          ref={matchProductRef}
          items={originOrder.items}
          supplierApi={supplier?.wmsApi ?? ''}
          setMatchErr={setMatchErr}
          errCodes={errCodes}
        />
      ),
    },
    {
      title: t("requirement.complete"),
      content: (
        <Tabs
          type="card" items={[
            {
              label: <SText text={t('sale.swapRes')} />,
              key: 'requirementList',
              children: (
                <ItemsOrderForm onClose={() => { }}
                  onFormSuccess={() => { }}
                  editMode="READ"
                  leftTitle={<SText text={resSaleOrder?.number ?? ''} />}
                  recordData={resSaleOrder || undefined}
                  noModal
                  wmsApi={supplier?.wmsApi}
                />
              )
            },
            {
              label: <SText text={t('sale.originSale')} />,
              key: 'preOutInventory',
              children: (
                <ItemsOrderForm onClose={() => { }}
                  onFormSuccess={() => { }}
                  editMode="READ"
                  leftTitle={<SText text={originOrder?.number ?? ''} />}
                  recordData={originOrder || undefined}
                  noModal
                />
              )
            },

          ]}
        />
      ),
    }
  ], [supplier, originOrder.items, errCodes, resSaleOrder, originOrder, t]);

  // Modal footer
  const footer = (
    <Flex justify="space-between">
      <Space>
        {current > 0 && current !== 2 && (
          <Button disabled={disPrev} onClick={handlePrev}>
            {t("requirement.prevStep")}
          </Button>
        )}
        {current !== 2 && (
          <Button disabled={disNext} type="primary" onClick={handleNext}>
            {t("requirement.nextStep")}
          </Button>
        )}
      </Space>
      <Space>
        <Button onClick={handleClose}>{t("requirement.close")}</Button>
      </Space>
    </Flex>
  );

  return (
    <Modal
      title={t("requirement.swapSale")}
      open={open}
      onCancel={handleClose}
      centered
      width={1200}
      maskClosable={false}
      footer={footer}
    >
      <Spin spinning={submitLoading}>
        <ContentWrap>
          <Steps current={current} items={stepsItems} />
          {current !== 0 && supplier && (
            <Flex gap={20}>
              <Typography.Title level={4} style={{ marginTop: 8 }}>
                {t('purchase.supplierPrint')}{getSupplierName(supplier)}
              </Typography.Title>
              <Space>
                <Typography.Title level={5} style={{ marginTop: 8 }}>
                  {t('requirement.orgLabel')}
                </Typography.Title>
                <SOrgSelect
                  disabled={current === 2}
                  allowClear={false}
                  onChange={id => {
                    setOrgId(id);
                    setCustomer(null);
                  }}
                  value={orgId || undefined}
                  list={orgList}
                  loading={orgListLoading}
                  style={{ minWidth: 220 }}
                />
              </Space>
              {!!orgId && supplier && (
                <Space>
                  <Typography.Title level={5} style={{ marginTop: 8 }}>
                    {t('sale.customerInfo')}：
                  </Typography.Title>
                  <SelectCustomer
                    supplierApi={supplier.wmsApi}
                    orgId={orgId}
                    disabled={current === 2 || !!!orgId}
                    onChangeCustomer={handleCustomerChange}
                    value={customer?.id ?? undefined}
                    style={{ minWidth: 220 }}
                    defaultList={customer ? [customer] : undefined}
                  />
                </Space>
              )}
            </Flex>
          )}
          <div className="body-content">
            {stepsItems[current].content}
          </div>
        </ContentWrap>
      </Spin>
    </Modal>
  );
};

export default SaleSwapModal;